import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Typy dla pól sortowania w każdej kategorii
export type PositionSortField = 
  | 'default' 
  | 'symbol' 
  | 'ticket' 
  | 'type' 
  | 'volume' 
  | 'openTime' 
  | 'closeTime' 
  | 'profit'

export type OrderSortField = 
  | 'default' 
  | 'symbol' 
  | 'order' 
  | 'type' 
  | 'volume' 
  | 'openTime' 
  | 'closeTime' 
  | 'state'

export type DealSortField = 
  | 'default' 
  | 'symbol' 
  | 'deal' 
  | 'position' 
  | 'type' 
  | 'volume' 
  | 'time' 
  | 'profit'

export type SortDirection = 'asc' | 'desc'

interface SortConfig<T> {
  field: T
  direction: SortDirection
}

interface SortState {
  // Sortowanie dla Positions
  positionsSort: SortConfig<PositionSortField> | null
  setPositionsSort: (field: PositionSortField, direction?: SortDirection) => void
  
  // Sortowanie dla Orders
  ordersSort: SortConfig<OrderSortField> | null
  setOrdersSort: (field: OrderSortField, direction?: SortDirection) => void
  
  // Sortowanie dla Deals
  dealsSort: SortConfig<DealSortField> | null
  setDealsSort: (field: DealSortField, direction?: SortDirection) => void
  
  // Reset sortowania dla konkretnej kategorii
  resetPositionsSort: () => void
  resetOrdersSort: () => void
  resetDealsSort: () => void
  
  // Reset wszystkich sortowań
  resetAllSorts: () => void
}

export const useSortStore = create<SortState>()(
  persist(
    (set) => ({
      // Positions
      positionsSort: { field: 'default', direction: 'asc' },
      setPositionsSort: (field, direction) => {
        // Dla 'default' nie ustawiamy kierunku sortowania
        if (field === 'default') {
          set({ positionsSort: { field: 'default', direction: 'asc' } })
          return
        }
        
        set((state) => {
          // Jeśli klikamy na to samo pole, zmieniamy kierunek
          if (state.positionsSort?.field === field) {
            const newDirection = state.positionsSort.direction === 'asc' ? 'desc' : 'asc'
            return { positionsSort: { field, direction: newDirection } }
          }
          // Nowe pole - domyślnie 'asc' lub podany kierunek
          return { positionsSort: { field, direction: direction || 'asc' } }
        })
      },
      
      // Orders
      ordersSort: { field: 'default', direction: 'asc' },
      setOrdersSort: (field, direction) => {
        if (field === 'default') {
          set({ ordersSort: { field: 'default', direction: 'asc' } })
          return
        }
        
        set((state) => {
          if (state.ordersSort?.field === field) {
            const newDirection = state.ordersSort.direction === 'asc' ? 'desc' : 'asc'
            return { ordersSort: { field, direction: newDirection } }
          }
          return { ordersSort: { field, direction: direction || 'asc' } }
        })
      },
      
      // Deals
      dealsSort: { field: 'default', direction: 'asc' },
      setDealsSort: (field, direction) => {
        if (field === 'default') {
          set({ dealsSort: { field: 'default', direction: 'asc' } })
          return
        }
        
        set((state) => {
          if (state.dealsSort?.field === field) {
            const newDirection = state.dealsSort.direction === 'asc' ? 'desc' : 'asc'
            return { dealsSort: { field, direction: newDirection } }
          }
          return { dealsSort: { field, direction: direction || 'asc' } }
        })
      },
      
      // Reset functions
      resetPositionsSort: () => set({ positionsSort: { field: 'default', direction: 'asc' } }),
      resetOrdersSort: () => set({ ordersSort: { field: 'default', direction: 'asc' } }),
      resetDealsSort: () => set({ dealsSort: { field: 'default', direction: 'asc' } }),
      
      resetAllSorts: () => set({
        positionsSort: { field: 'default', direction: 'asc' },
        ordersSort: { field: 'default', direction: 'asc' },
        dealsSort: { field: 'default', direction: 'asc' }
      })
    }),
    {
      name: 'sort-storage',
      // Persistujemy wszystkie sortowania
      partialize: (state) => ({
        positionsSort: state.positionsSort,
        ordersSort: state.ordersSort,
        dealsSort: state.dealsSort
      })
    }
  )
)

// Selektory dla optymalizacji re-renderów
export const usePositionsSort = () => useSortStore((state) => state.positionsSort)
export const useOrdersSort = () => useSortStore((state) => state.ordersSort)
export const useDealsSort = () => useSortStore((state) => state.dealsSort)

// Helper do mapowania pól na labele UI
export const SORT_FIELD_LABELS = {
  positions: {
    default: 'Default',
    symbol: 'Symbol',
    ticket: 'Ticket',
    type: 'Type',
    volume: 'Volume',
    openTime: 'Open Time',
    closeTime: 'Close Time',
    profit: 'Profit'
  },
  orders: {
    default: 'Default',
    symbol: 'Symbol',
    order: 'Order',
    type: 'Type',
    volume: 'Volume',
    openTime: 'Open Time',
    closeTime: 'Close Time',
    state: 'State'
  },
  deals: {
    default: 'Default',
    symbol: 'Symbol',
    deal: 'Deal',
    position: 'Position',
    type: 'Type',
    volume: 'Volume',
    time: 'Time',
    profit: 'Profit'
  }
} as const
